import { c as _c } from "react/compiler-runtime";
// Задание ИИ-загрузки (/catalog-import/:id): материалы → разбор → проверка →
// загрузка. Текущий шаг — из статуса задания; пройденные шаги кликабельны,
// чтобы вернуться посмотреть материалы или итог разбора.
import { ArrowLeft, Check, CircleX, Loader2, Pencil, WandSparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { CatalogImportFile, CatalogImportJobDetail } from "@osmix/contracts";
import { useCancelImportJob, useCatalogImportJob, useCatalogImportModels, useParseImport, useUpdateImportJob } from "@/api/hooks/use-catalog-import";
import { CommitStep } from "@/components/catalog-import/commit-step";
import { ImportFileViewer } from "@/components/catalog-import/file-viewer";
import { ItemEditor } from "@/components/catalog-import/item-editor";
import { MaterialsStep } from "@/components/catalog-import/materials-step";
import { ParseStep } from "@/components/catalog-import/parse-step";
import { ReviewStep } from "@/components/catalog-import/review-step";
import { JobStatusPill, StepHeader, type WizardStep, errorText, formatDate, formatUsd, targetText, useShopHref } from "@/components/catalog-import/shared";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Skeleton } from "@osmix/ui/skeleton";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
const STEP_LABELS: Record<WizardStep, string> = {
  materials: "Материалы",
  parse: "Разбор",
  review: "Проверка",
  commit: "Загрузка"
};

/** Шаг, на котором задание сейчас. */
const stepOf = (job: CatalogImportJobDetail): WizardStep => {
  switch (job.status) {
    case "draft":
    case "cancelled":
      return "materials";
    case "parsing":
      return "parse";
    case "review":
      return "review";
    case "committing":
    case "done":
      return "commit";
    case "failed":
      return job.progress.stage === "commit" ? "commit" : "parse";
  }
};
const reachable = (job: CatalogImportJobDetail, step: WizardStep): boolean => {
  if (job.status === "cancelled") return false;
  switch (step) {
    case "materials":
      return true;
    case "parse":
      return job.status !== "draft";
    case "review":
      return job.items.length > 0 && job.status !== "parsing";
    case "commit":
      return job.status === "committing" || job.status === "done" || job.status === "failed" && job.progress.stage === "commit";
  }
};
export function CatalogImportJobPage() {
  const $ = _c(9);
  const {
    id: t0
  } = useParams();
  const id = t0 === undefined ? "" : t0;
  const query = useCatalogImportJob(id);
  const job = query.data;
  const t1 = useAuth(_temp);
  let t2;
  if ($[0] !== t1) {
    t2 = hasAdminPermission(t1, "action.catalog_import.run");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canEdit = t2;
  if (query.error && !job) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <BackLink />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== query.error.message) {
      t4 = <div className="flex flex-col items-start gap-3">{t3}<div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{query.error.message}</div></div>;
      $[3] = query.error.message;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  if (!job) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex flex-col gap-4"><BackLink /><Skeleton className="h-8 w-2/3" /><Skeleton className="h-64 w-full" /></div>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== canEdit || $[7] !== job) {
    t3 = <JobWizard job={job} canEdit={canEdit} />;
    $[6] = canEdit;
    $[7] = job;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(s) {
  return s.user;
}
function BackLink() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Button variant="outline" size="sm" asChild={true} className="w-fit"><Link to="/catalog-import"><ArrowLeft className="size-4" /> Все загрузки</Link></Button>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function JobWizard(t0) {
  const $ = _c(70);
  const {
    job,
    canEdit
  } = t0;
  let t1;
  if ($[0] !== job) {
    t1 = stepOf(job);
    $[0] = job;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const current = t1;
  const [viewing, setViewing] = useState(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setViewing(null);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== job.status) {
    t3 = [job.status];
    $[3] = job.status;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== current || $[6] !== job || $[7] !== viewing) {
    t4 = viewing && reachable(job, viewing) ? viewing : current;
    $[5] = current;
    $[6] = job;
    $[7] = viewing;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const step = t4;
  const [editing, setEditing] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState(null);
  const cancel = useCancelImportJob(job.id);
  const parse = useParseImport(job.id);
  let t5;
  if ($[9] !== canEdit || $[10] !== job.status) {
    t5 = canEdit && ["draft", "parsing", "review", "failed"].includes(job.status);
    $[9] = canEdit;
    $[10] = job.status;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const cancellable = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (itemId, order) => setEditing({
      id: itemId,
      order
    });
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const openItem = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (file, locator) => setViewer({
      file,
      locator
    });
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const openFile = t7;
  let t8;
  if ($[14] !== parse) {
    t8 = async () => {
      setError(null);
      ;
      try {
        await parse.mutateAsync();
      } catch (t9) {
        const e = t9;
        setError(errorText(e));
      }
    };
    $[14] = parse;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const reparse = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <BackLink />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== cancellable) {
    t10 = cancellable ? () => setConfirmCancel(true) : undefined;
    $[17] = cancellable;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== canEdit || $[20] !== job || $[21] !== t10) {
    t11 = <JobHeader job={job} canEdit={canEdit} onCancel={t10} />;
    $[19] = canEdit;
    $[20] = job;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== current || $[24] !== job || $[25] !== step) {
    t12 = job.status !== "cancelled" && <StepHeader steps={(["materials", "parse", "review", "commit"] as const).map(key => ({
      key,
      label: STEP_LABELS[key],
      reachable: reachable(job, key)
    }))} current={current} viewing={step} complete={job.status === "done"} onSelect={setViewing} />;
    $[23] = current;
    $[24] = job;
    $[25] = step;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== canEdit || $[28] !== error || $[29] !== job.error || $[30] !== job.files || $[31] !== job.progress || $[32] !== job.status || $[33] !== parse || $[34] !== reparse || $[35] !== step) {
    t13 = job.status === "failed" && <div className="flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 sm:flex-row sm:items-start sm:justify-between"><div className="flex min-w-0 gap-3"><CircleX className="mt-0.5 size-5 shrink-0 text-destructive" /><div className="min-w-0"><p className="font-medium">{job.progress.stage === "commit" ? "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0441\u043E\u0440\u0432\u0430\u043B\u0430\u0441\u044C" : "\u0420\u0430\u0437\u0431\u043E\u0440 \u0441\u043E\u0440\u0432\u0430\u043B\u0441\u044F"}</p><p className="text-sm text-muted-foreground">{job.error ?? "\u041F\u0440\u0438\u0447\u0438\u043D\u0443 \u0448\u043B\u044E\u0437 \u043D\u0435 \u0441\u043E\u043E\u0431\u0449\u0438\u043B."}</p>{error && <p className="mt-1 text-sm text-destructive">{error}</p>}</div></div>{canEdit && <div className="flex shrink-0 flex-wrap gap-2">{step !== "materials" && <Button variant="outline" onClick={() => setViewing("materials")}>К материалам</Button>}<Button disabled={parse.isPending || job.files.length === 0} onClick={() => void reparse()}>{parse.isPending ? <Loader2 className="size-4 animate-spin" /> : <WandSparkles className="size-4" />}Разобрать заново</Button></div>}</div>;
    $[27] = canEdit;
    $[28] = error;
    $[29] = job.error;
    $[30] = job.files;
    $[31] = job.progress;
    $[32] = job.status;
    $[33] = parse;
    $[34] = reparse;
    $[35] = step;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== canEdit || $[38] !== job || $[39] !== step) {
    t14 = job.status === "cancelled" ? <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-6"><p className="font-medium">Задание отменено {formatDate(job.updatedAt)}</p><p className="text-sm text-muted-foreground">Материалы и черновики удалены. Чтобы загрузить каталог, заведите новое задание.</p><Button asChild={true} variant="outline"><Link to="/catalog-import">К списку загрузок</Link></Button></div> : step === "materials" ? <MaterialsStep job={job} canEdit={canEdit} onOpenFile={f => openFile(f)} onParsed={() => setViewing(null)} /> : step === "parse" ? <ParseStep job={job} canEdit={canEdit} onCancel={() => setConfirmCancel(true)} /> : step === "review" ? <ReviewStep job={job} canEdit={canEdit} onOpenItem={openItem} /> : <CommitStep job={job} canEdit={canEdit} onOpenItem={openItem} />;
    $[37] = canEdit;
    $[38] = job;
    $[39] = step;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = editing?.id ?? null;
  let t16;
  if ($[41] !== editing?.order) {
    t16 = editing?.order ?? [];
    $[41] = editing?.order;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = itemId_0 => setEditing(e_0 => e_0 ? {
      ...e_0,
      id: itemId_0
    } : e_0);
    t18 = () => setEditing(null);
    $[43] = t17;
    $[44] = t18;
  } else {
    t17 = $[43];
    t18 = $[44];
  }
  let t19;
  if ($[45] !== canEdit || $[46] !== job || $[47] !== t15 || $[48] !== t16) {
    t19 = <ItemEditor job={job} itemId={t15} siblings={t16} canEdit={canEdit} onNavigate={t17} onClose={t18} onOpenFile={openFile} />;
    $[45] = canEdit;
    $[46] = job;
    $[47] = t15;
    $[48] = t16;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = viewer?.file ?? null;
  const t21 = viewer?.locator;
  let t22;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setViewer(null);
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== job.id || $[52] !== t20 || $[53] !== t21) {
    t23 = <ImportFileViewer jobId={job.id} file={t20} locator={t21} onClose={t22} />;
    $[51] = job.id;
    $[52] = t20;
    $[53] = t21;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== cancel) {
    t24 = () => cancel.mutate(undefined, {
      onSuccess: () => setConfirmCancel(false),
      onError: e_1 => {
        setError(errorText(e_1));
        setConfirmCancel(false);
      }
    });
    $[55] = cancel;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setConfirmCancel(false);
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== cancel.isPending || $[59] !== confirmCancel || $[60] !== t24) {
    t26 = <ConfirmDialog open={confirmCancel} busy={cancel.isPending} title={"\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u0434\u0430\u043D\u0438\u0435?"} description={"\u041C\u0430\u0442\u0435\u0440\u0438\u0430\u043B\u044B \u0438 \u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u0438 \u043F\u043E\u0437\u0438\u0446\u0438\u0439 \u0443\u0434\u0430\u043B\u044F\u0442\u0441\u044F, \u0440\u0430\u0441\u0445\u043E\u0434 \u043D\u0430 \u043C\u043E\u0434\u0435\u043B\u044C \u043E\u0441\u0442\u0430\u043D\u0435\u0442\u0441\u044F \u0432 \u0437\u0430\u0434\u0430\u043D\u0438\u0438. \u0412\u0435\u0440\u043D\u0443\u0442\u044C \u0437\u0430\u0434\u0430\u043D\u0438\u0435 \u043D\u0435\u043B\u044C\u0437\u044F."} confirmLabel={"\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u0434\u0430\u043D\u0438\u0435"} cancelLabel={"\u041D\u0435 \u043E\u0442\u043C\u0435\u043D\u044F\u0442\u044C"} confirmVariant="destructive" onConfirm={t24} onClose={t25} />;
    $[58] = cancel.isPending;
    $[59] = confirmCancel;
    $[60] = t24;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14 || $[66] !== t19 || $[67] !== t23 || $[68] !== t26) {
    t27 = <div className="flex flex-col gap-4">{t9}{t11}{t12}{t13}{t14}{t19}{t23}{t26}</div>;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t19;
    $[67] = t23;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  return t27;
}
function JobHeader(t0) {
  const $ = _c(49);
  const {
    job,
    canEdit,
    onCancel
  } = t0;
  const update = useUpdateImportJob(job.id);
  const model = useCatalogImportModels().data?.models.find(m => m.id === job.model);
  const shopHref = useShopHref(job.target.kind === "existing_shop" ? job.target.shopId : job.shop?.id);
  const [title, setTitle] = useState(null);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== job.title || $[1] !== title || $[2] !== update) {
    t1 = async () => {
      if (title == null) {
        return;
      }
      const next = title.trim();
      if (!next || next === job.title) {
        setTitle(null);
        return;
      }
      ;
      try {
        await update.mutateAsync({
          title: next
        });
        setTitle(null);
        setError(null);
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = job.title;
    $[1] = title;
    $[2] = update;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const saveTitle = t1;
  let t2;
  if ($[4] !== canEdit || $[5] !== job.status || $[6] !== job.title || $[7] !== saveTitle || $[8] !== title || $[9] !== update) {
    t2 = title == null ? <div className="flex min-w-0 items-start gap-2"><h1 className="min-w-0 text-2xl font-bold tracking-tight break-words">{job.title}</h1>{canEdit && job.status !== "cancelled" && <Button variant="ghost" size="icon-sm" className="mt-0.5 shrink-0" aria-label={"\u041F\u0435\u0440\u0435\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u0442\u044C \u0437\u0430\u0434\u0430\u043D\u0438\u0435"} onClick={() => setTitle(job.title)}><Pencil className="size-4" /></Button>}</div> : <form className="flex max-w-xl items-center gap-2" onSubmit={e_0 => {
      e_0.preventDefault();
      saveTitle();
    }}><Input value={title} autoFocus={true} onChange={e_1 => setTitle(e_1.target.value)} onKeyDown={e_2 => e_2.key === "Escape" && setTitle(null)} aria-label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0437\u0430\u0434\u0430\u043D\u0438\u044F"} className="h-9" /><Button type="submit" size="icon-sm" aria-label={"\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"} disabled={update.isPending}><Check className="size-4" /></Button><Button type="button" variant="ghost" size="icon-sm" aria-label={"\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u043F\u0435\u0440\u0435\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u043D\u0438\u0435"} onClick={() => setTitle(null)}><X className="size-4" /></Button></form>;
    $[4] = canEdit;
    $[5] = job.status;
    $[6] = job.title;
    $[7] = saveTitle;
    $[8] = title;
    $[9] = update;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== error) {
    t3 = error && <p className="text-sm text-destructive">{error}</p>;
    $[11] = error;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== job.status) {
    t4 = <JobStatusPill status={job.status} />;
    $[13] = job.status;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== job || $[16] !== shopHref) {
    t5 = job.target.kind === "existing_shop" && shopHref ? <Link to={shopHref} className="text-primary hover:underline">{targetText(job)}</Link> : job.application ? <>новый магазин из{" "}<Link to={`/applications/${job.application.id}`} className="text-primary hover:underline">заявки «{job.application.shopName}»</Link></> : targetText(job);
    $[15] = job;
    $[16] = shopHref;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = <span className="min-w-0">Куда:{" "}{t5}</span>;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = model?.label ?? job.model;
  let t8;
  if ($[20] !== t7) {
    t8 = <span>Модель: {t7}</span>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = job.createdBy?.name ?? "\u2014";
  let t10;
  if ($[22] !== job.createdAt) {
    t10 = formatDate(job.createdAt);
    $[22] = job.createdAt;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <span>{t9}, {t10}</span>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== job.usage.costUsd) {
    t12 = formatUsd(job.usage.costUsd);
    $[27] = job.usage.costUsd;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <span className="tabular-nums">Потрачено: {t12}</span>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== t4 || $[34] !== t6 || $[35] !== t8) {
    t14 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">{t4}{t6}{t8}{t11}{t13}</div>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t4;
    $[34] = t6;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== job.instructions) {
    t15 = job.instructions && <p className="max-w-3xl text-sm"><span className="text-muted-foreground">Подсказка модели: </span>{job.instructions}</p>;
    $[37] = job.instructions;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15 || $[41] !== t2 || $[42] !== t3) {
    t16 = <div className="min-w-0 space-y-1">{t2}{t3}{t14}{t15}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t2;
    $[42] = t3;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== onCancel) {
    t17 = onCancel && <Button variant="outline" className="shrink-0 text-destructive" onClick={onCancel}>Отменить задание</Button>;
    $[44] = onCancel;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">{t16}{t17}</header>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
}